import { createContext, useContext, useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { t } from '@lingui/macro';

import { Alert, AlertDescription } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import { cn } from 'lib/utils';

// Context holds all state related to the display of Alert messages
export const NotificationContext = createContext();

// Hook allows react components to access Notification data and functions
export function useNotification() {
    return useContext(NotificationContext);
}

const variantMap = {
    success: 'success',
    error: 'destructive',
    warning: 'warning',
    info: 'info',
};

// Provider wraps the App component with the Notification Context
// Any child react components will be able to hook into this data
export function NotificationProvider(props) {
    // State
    const [notification, setNotification] = useState({ open: false, message: '', variant: 'info' });

    // Setters
    function createNotification(message, options) {
        const variant = options?.variant || 'info';

        setNotification({ open: true, message, variant: variant });
    }

    function closeNotification() {
        setNotification((s) => ({ ...s, open: false, message: '' }));
    }

    return (
        <NotificationContext.Provider
            value={{ notification, createNotification, closeNotification }}
        >
            {props.children}
        </NotificationContext.Provider>
    );
}

// Notification Component: Displays an alert when notification is activated, and a message is provided
export function Notification() {
    const { notification, closeNotification } = useNotification();

    useEffect(() => {
        if (!notification.open) {
            return undefined;
        }

        const timer = window.setTimeout(() => {
            closeNotification();
        }, 5000);

        return () => window.clearTimeout(timer);
    }, [notification.open, notification.message, closeNotification]);

    if (!notification.open || !notification.message) {
        return null;
    }

    const alertVariant = variantMap[notification.variant] || 'info';

    return (
        <div className="pointer-events-none fixed bottom-4 left-4 z-[20000] max-w-sm">
            <Alert variant={alertVariant} className={cn('pointer-events-auto pr-10 shadow-lg')}>
                <AlertDescription>{notification.message}</AlertDescription>
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={closeNotification}
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 hover:bg-black/10"
                    aria-label={t`Close notification`}
                >
                    <X className="h-4 w-4" />
                </Button>
            </Alert>
        </div>
    );
}
